import { useMutation, useQuery } from '@apollo/client';
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Drawer,
  Dialog,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
} from '@mui/material';
import { useState, useCallback } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import {
  encryptItemKeyForRecipient,
  decodePublicKey,
} from '../../crypto/encryption';
import {
  VAULT_ITEM_QUERY,
  SHARE_VAULT_ITEM_MUTATION,
  REVOKE_SHARE_MUTATION,
  WORKSPACE_SHARE_CANDIDATES_QUERY,
} from '../../services/graphql';
import type { VaultItemShare } from '../../types';

interface ShareSheetProps {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemKey: Uint8Array;
  shares: VaultItemShare[];
}

// Type for share candidate from the API
interface ShareCandidate {
  userId: string;
  email: string;
  publicKey: string | null;
}

export default function ShareSheet({
  open,
  onClose,
  itemId,
  itemKey,
  shares,
}: ShareSheetProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, privateKey } = useAuth();
  const { currentWorkspace } = useWorkspace();

  const [selectedUserId, setSelectedUserId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const workspaceId = currentWorkspace?.id;

  // Fetch share candidates for the current workspace
  const { data: candidatesData, loading: candidatesLoading } = useQuery(
    WORKSPACE_SHARE_CANDIDATES_QUERY,
    {
      variables: { workspaceId },
      skip: !open || !workspaceId, // Only fetch when dialog is open and workspace is selected
    }
  );

  const shareCandidates: ShareCandidate[] = candidatesData?.workspaceShareCandidates || [];

  const [shareVaultItem, { loading: shareLoading }] = useMutation(SHARE_VAULT_ITEM_MUTATION, {
    refetchQueries: [{ query: VAULT_ITEM_QUERY, variables: { id: itemId } }],
    onError: (err) => {
      setError(err.message);
    },
  });

  const [revokeShare, { loading: revokeLoading }] = useMutation(REVOKE_SHARE_MUTATION, {
    refetchQueries: [{ query: VAULT_ITEM_QUERY, variables: { id: itemId } }],
    onCompleted: () => setSuccess('Share revoked'),
    onError: (err) => setError(err.message),
  });

  const handleShare = useCallback(async () => {
    setError('');
    setSuccess('');

    if (!selectedUserId) {
      setError('Please select a member');
      return;
    }

    // Find selected candidate
    const selectedCandidate = shareCandidates.find((c) => c.userId === selectedUserId);
    if (!selectedCandidate) {
      setError('Selected member not found');
      return;
    }

    const recipientEmail = selectedCandidate.email;
    const recipientPublicKey = selectedCandidate.publicKey;

    if (!privateKey) {
      setError('Private key not available. Please re-login.');
      return;
    }

    if (!recipientPublicKey) {
      setError('User does not have a public key. Please ensure they have completed account setup.');
      return;
    }

    // Encrypt item key for recipient
    const recipientPubKeyBytes = decodePublicKey(recipientPublicKey);
    const { encryptedKey, nonce } = encryptItemKeyForRecipient(
      itemKey,
      recipientPubKeyBytes,
      privateKey
    );

    // Share with existing user
    await shareVaultItem({
      variables: {
        input: {
          itemId,
          recipientEmail,
          encryptedItemKey: encryptedKey,
          itemKeyNonce: nonce,
        },
      },
    });

    setSelectedUserId('');
    setSuccess('Shared successfully!');
  }, [selectedUserId, shareCandidates, privateKey, itemKey, itemId, shareVaultItem]);

  const handleRevokeShare = useCallback(async (shareId: string) => {
    await revokeShare({ variables: { input: { shareId } } });
  }, [revokeShare]);

  const loading = shareLoading || revokeLoading;

  // Determine helper text based on state
  const getHelperText = () => {
    if (!workspaceId) {
      return 'No workspace selected. Please select a workspace first.';
    }
    if (candidatesLoading) {
      return 'Loading members...';
    }
    if (shareCandidates.length === 0) {
      return 'No other members in this workspace yet. Invite members to share items.';
    }
    return 'Select a member from the list to share this item.';
  };

  const content = (
    <Box sx={{ p: 3, minHeight: '50vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Share Item
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Share with someone section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          <PersonAddIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 1 }} />
          Share with someone
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {candidatesLoading ? (
            <Skeleton variant="rounded" width="100%" height={40} />
          ) : (
            <FormControl fullWidth size="small" disabled={loading || !workspaceId}>
              <InputLabel>Select Member</InputLabel>
              <Select
                value={selectedUserId}
                label="Select Member"
                onChange={(e) => setSelectedUserId(e.target.value)}
                disabled={loading || !workspaceId}
              >
                {shareCandidates.length === 0 ? (
                  <MenuItem disabled>No members available</MenuItem>
                ) : (
                  shareCandidates.map((candidate) => (
                    <MenuItem key={candidate.userId} value={candidate.userId}>
                      {candidate.email}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            onClick={handleShare}
            disabled={loading || !selectedUserId || candidatesLoading || !workspaceId}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{ minWidth: 100 }}
          >
            Share
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {getHelperText()}
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Shared with section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Shared with ({shares.length})
        </Typography>

        {shares.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Not shared with anyone yet
          </Typography>
        ) : (
          <List dense>
            {shares.map((share) => (
              <ListItem key={share.id}>
                <ListItemText
                  primary={share.recipientEmail}
                  secondary={
                    share.recipientEmail === user?.email ? 'Owner' :
                    `Shared ${new Date(share.sharedAt).toLocaleDateString()}`
                  }
                />
                <ListItemSecondaryAction>
                  <Chip
                    size="small"
                    label={share.status}
                    color={share.status === 'accepted' ? 'success' : 'warning'}
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleRevokeShare(share.id)}
                    disabled={loading}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Snackbar for success messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        message={success}
      />
    </Box>
  );

  // Use Drawer on mobile, Dialog on desktop
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '90vh' },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {content}
    </Dialog>
  );
}

